import React, { useEffect, useState } from 'react';
import { Calendar, Clock, DollarSign, Wrench, Settings, Users } from 'lucide-react';

const WelcomePage = ({ setCurrentPage, services, adminSettings }) => {
    const [selectedServiceCategory, setSelectedServiceCategory] = useState(null);

    // Check for URL parameters on load
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const serviceParam = urlParams.get('service');
        const directBook = urlParams.get('book');

        // If user wants to book directly, skip welcome page
        if (directBook === 'now' || directBook === 'true' || directBook === '1') {
            setCurrentPage('schedule');
            return;
        }

        // Pre-select service category if specified
        if (serviceParam) {
            setSelectedServiceCategory(serviceParam);
        }
    }, [setCurrentPage]);

    // Group services by category for better organization
    const groupServicesByCategory = () => {
        const grouped = {};
        services.forEach(service => {
            // Extract category from service name or use a default
            const category = extractServiceCategory(service.name);
            if (!grouped[category]) {
                grouped[category] = [];
            }
            grouped[category].push(service);
        });
        return grouped;
    };

    // Helper function to determine service category
    const extractServiceCategory = (serviceName) => {
        const name = serviceName.toLowerCase();
        if (name.includes('sprinkler') || name.includes('irrigation')) return 'sprinkler';
        if (name.includes('plumb')) return 'plumbing';
        if (name.includes('electric') || name.includes('electrical')) return 'electrical';
        if (name.includes('paint')) return 'painting';
        if (name.includes('repair') || name.includes('fix')) return 'repairs';
        if (name.includes('install')) return 'installation';
        return 'general';
    };

    // Get category display info
    const getCategoryInfo = (category) => {
        const categoryMap = {
            sprinkler: {
                title: 'Sprinkler Systems',
                icon: Calendar,
                color: 'green',
                description: 'Spring startups, fall winterizations, repairs & maintenance'
            },
            plumbing: {
                title: 'Plumbing Services',
                icon: Wrench,
                color: 'blue',
                description: 'Pipes, fixtures, water heaters, and drain cleaning'
            },
            electrical: {
                title: 'Electrical Work',
                icon: Settings,
                color: 'yellow',
                description: 'Outlets, switches, lighting, and electrical repairs'
            },
            painting: {
                title: 'Painting Services',
                icon: Users,
                color: 'purple',
                description: 'Interior and exterior painting, touch-ups'
            },
            repairs: {
                title: 'General Repairs',
                icon: Wrench,
                color: 'red',
                description: 'Fix-it services for your home and property'
            },
            installation: {
                title: 'Installation Services',
                icon: Settings,
                color: 'indigo',
                description: 'Professional installation of various home components'
            },
            general: {
                title: 'Handyman Services',
                icon: Wrench,
                color: 'gray',
                description: 'General maintenance and home improvement'
            }
        };
        return categoryMap[category] || categoryMap.general;
    };

    const groupedServices = groupServicesByCategory();

    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50">
            <div className="container mx-auto px-4 py-8">
                <div className="max-w-6xl mx-auto text-center">

                    {/* Header with Logo */}
                    <div className="mb-12">
                        <div className="flex justify-center mb-6">
                            <img
                                src="/shared/images/Alltech-Logo.png"
                                alt="Alltech LLC Logo"
                                className="h-20 w-auto"
                                onError={(e) => {
                                    // Fallback if logo doesn't load
                                    e.target.style.display = 'none';
                                }}
                            />
                        </div>
                        <h1 className="text-5xl font-bold text-gray-900 mb-4">
                            {adminSettings?.business_name || 'Alltech LLC'}
                        </h1>
                        <p className="text-xl text-gray-600 mb-2">Professional Handyman & Home Services</p>
                        <p className="text-gray-500">Reliable • Professional • Affordable</p>
                    </div>

                    {/* Main CTA Section */}
                    <div className="bg-white rounded-2xl shadow-2xl p-8 mb-8">
                        <div className="grid md:grid-cols-2 gap-8 items-center">
                            <div className="text-left">
                                <h2 className="text-3xl font-bold text-gray-800 mb-4">
                                    Expert Home Services You Can Trust
                                </h2>
                                <p className="text-gray-600 mb-6">
                                    From seasonal maintenance to emergency repairs, we provide professional
                                    handyman services to keep your home in perfect condition year-round.
                                </p>
                                <div className="space-y-3 mb-6">
                                    <div className="flex items-center text-green-600">
                                        <Calendar className="w-5 h-5 mr-3" />
                                        <span>Easy online scheduling</span>
                                    </div>
                                    <div className="flex items-center text-green-600">
                                        <DollarSign className="w-5 h-5 mr-3" />
                                        <span>Competitive, transparent pricing</span>
                                    </div>
                                    <div className="flex items-center text-green-600">
                                        <Clock className="w-5 h-5 mr-3" />
                                        <span>Flexible appointment times</span>
                                    </div>
                                    <div className="flex items-center text-green-600">
                                        <Wrench className="w-5 h-5 mr-3" />
                                        <span>Licensed and insured</span>
                                    </div>
                                </div>
                            </div>
                            <div className="bg-gradient-to-br from-green-100 to-blue-100 rounded-xl p-6">
                                <Calendar className="w-24 h-24 text-green-600 mx-auto mb-4" />
                                <h3 className="text-xl font-semibold text-gray-800 mb-2">Ready to Schedule?</h3>
                                <p className="text-gray-600 text-sm">Book your service appointment in just a few clicks</p>
                            </div>
                        </div>
                    </div>

                    {/* Service Categories */}
                    {Object.keys(groupedServices).length > 0 && (
                        <div className="mb-8">
                            <h2 className="text-2xl font-bold text-gray-800 mb-6">Our Services</h2>
                            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                                {Object.entries(groupedServices).map(([category, categoryServices]) => {
                                    const categoryInfo = getCategoryInfo(category);
                                    const IconComponent = categoryInfo.icon;

                                    return (
                                        <div
                                            key={category}
                                            className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-shadow cursor-pointer"
                                            onClick={() => setSelectedServiceCategory(category)}
                                        >
                                            <div className="text-center mb-4">
                                                <div className={`bg-${categoryInfo.color}-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-3`}>
                                                    <IconComponent className={`h-8 w-8 text-${categoryInfo.color}-600`} />
                                                </div>
                                                <h3 className="text-lg font-semibold text-gray-800 mb-2">
                                                    {categoryInfo.title}
                                                </h3>
                                                <p className="text-sm text-gray-600 mb-4">{categoryInfo.description}</p>
                                            </div>

                                            {/* Show services in this category */}
                                            <div className="text-left space-y-1">
                                                {categoryServices.slice(0, 3).map(service => (
                                                    <div key={service.id} className="text-xs text-gray-500 flex justify-between">
                                                        <span>{service.name}</span>
                                                        <span>${service.price}</span>
                                                    </div>
                                                ))}
                                                {categoryServices.length > 3 && (
                                                    <div className="text-xs text-gray-400 italic">
                                                        +{categoryServices.length - 3} more services
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* CTA Buttons */}
                    <div className="space-y-4">
                        <button
                            onClick={() => setCurrentPage('schedule')}
                            className="bg-gradient-to-r from-green-600 to-blue-600 hover:from-green-700 hover:to-blue-700 text-white text-xl font-semibold px-12 py-4 rounded-xl shadow-lg transform hover:scale-105 transition-all duration-200"
                        >
                            Schedule Appointment
                        </button>

                        {/* Quick service links */}
                        <div className="flex flex-wrap justify-center gap-4 mt-6">
                            <button
                                onClick={() => {
                                    // Set URL parameter for sprinkler and go to schedule
                                    window.history.pushState({}, '', '?service=sprinkler');
                                    setCurrentPage('schedule');
                                }}
                                className="bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-lg text-sm transition-colors"
                            >
                                Sprinkler Services
                            </button>
                            <button
                                onClick={() => {
                                    window.history.pushState({}, '', '?service=general');
                                    setCurrentPage('schedule');
                                }}
                                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg text-sm transition-colors"
                            >
                                General Repairs
                            </button>
                        </div>
                    </div>

                    {/* Business Info */}
                    <div className="mt-12 grid md:grid-cols-2 gap-6">
                        {/* Payment Info */}
                        <div className="bg-blue-50 rounded-xl p-6">
                            <h3 className="text-lg font-semibold text-blue-800 mb-3">Payment Options</h3>
                            <p className="text-blue-600">We accept Venmo, check, or cash payment on service completion</p>
                        </div>

                        {/* Service Area */}
                        <div className="bg-green-50 rounded-xl p-6">
                            <h3 className="text-lg font-semibold text-green-800 mb-3">Service Area</h3>
                            <p className="text-green-600">
                                {adminSettings?.business_address ?
                                    `Serving ${adminSettings.business_address} and surrounding areas` :
                                    'Proudly serving Idaho Falls and surrounding areas'
                                }
                            </p>
                        </div>
                    </div>

                    {/* Contact Info */}
                    {(adminSettings?.business_phone || adminSettings?.admin_email) && (
                        <div className="mt-8 bg-gray-50 rounded-xl p-6">
                            <h3 className="text-lg font-semibold text-gray-800 mb-3">Contact Information</h3>
                            <div className="flex flex-wrap justify-center gap-6 text-gray-600">
                                {adminSettings.business_phone && (
                                    <div className="flex items-center">
                                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                                        </svg>
                                        <a href={`tel:${adminSettings.business_phone}`} className="hover:text-green-600">
                                            {adminSettings.business_phone}
                                        </a>
                                    </div>
                                )}
                                {adminSettings.admin_email && (
                                    <div className="flex items-center">
                                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                        </svg>
                                        <a href={`mailto:${adminSettings.admin_email}`} className="hover:text-green-600">
                                            {adminSettings.admin_email}
                                        </a>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default WelcomePage;